import { useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Award,
  Calendar,
  Heart,
  LayoutDashboard,
  Map,
  MessageCircle,
  Settings,
  Star,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { DashboardSidebar } from './DashboardSidebar';
import { pageTransition } from '@/lib/motion/variants';
import { brand } from '@/lib/theme/tokens';
import { cn } from '@/lib/utils/cn';

type Tab = { to: string; label: string; icon: LucideIcon; badge?: string; end?: boolean };

const TABS: Tab[] = [
  { to: '/dashboard',               label: 'Overview',   icon: LayoutDashboard, end: true },
  { to: '/dashboard/bookings',      label: 'Bookings',   icon: Calendar,        badge: '2' },
  { to: '/dashboard/wishlists',     label: 'Wishlists',  icon: Heart },
  { to: '/dashboard/reviews',       label: 'Reviews',    icon: Star },
  { to: '/dashboard/rewards',       label: 'Rewards',    icon: Award },
  { to: '/dashboard/community',     label: 'Companions', icon: Users },
  { to: '/dashboard/trip-planner',  label: 'Trip',       icon: Map },
  { to: '/dashboard/messages',      label: 'Messages',   icon: MessageCircle,   badge: '2' },
  { to: '/dashboard/settings',      label: 'Settings',   icon: Settings },
];

function MobileTabs() {
  const { pathname } = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const active = container.querySelector('[aria-current="page"]') as HTMLElement | null;
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [pathname]);

  return (
    <div
      className="sticky top-[68px] z-40 -mx-4 border-b border-[var(--glass-edge)]"
      style={{ background: 'var(--popover)' }}
    >
      <div
        ref={scrollRef}
        className="flex gap-1 overflow-x-auto px-4 py-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                cn(
                  'relative flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors',
                  isActive ? 'text-white' : 'text-ink-dim hover:text-ink',
                )
              }
              style={({ isActive }) => ({
                background: isActive ? brand.teal : 'transparent',
              })}
            >
              {({ isActive }) => (
                <>
                  <Icon size={13} className="shrink-0" color={isActive ? '#fff' : 'currentColor'} />
                  <span>{tab.label}</span>
                  {tab.badge && !isActive && (
                    <span
                      className="ml-0.5 rounded-full px-1.5 py-px text-[9px] font-bold leading-none"
                      style={{ background: 'var(--glass-hi)', color: brand.teal }}
                    >
                      {tab.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
        {/* trailing spacer so the last tab isn't flush with the edge when scrolled */}
        <div className="w-2 shrink-0" aria-hidden />
      </div>
    </div>
  );
}

export function DashboardLayout() {
  return (
    <motion.div
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Mobile tab bar — hidden on lg+ */}
      <div className="lg:hidden">
        <MobileTabs />
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[240px_1fr]">
          {/* Sidebar — desktop only */}
          <aside className="hidden lg:block">
            <DashboardSidebar />
          </aside>

          <main className="min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </motion.div>
  );
}
